import React, { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Loading, Empty, SectionHeader, Card } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const QUOTA_FIELDS = [
  ["users_included", "Utilisateurs inclus"],
  ["data_sources", "Sources de données"],
  ["ai_usage", "Usage IA"],
  ["destinations_properties", "Destinations / établissements"],
  ["partner_access", "Accès partenaires"],
  ["campaign_features", "Campagnes"],
  ["reporting_level", "Reporting"],
  ["support_level", "Support"],
];

const emptyPlan = () => ({
  name: "", monthly_price: "", annual_price: "", currency: "EUR",
  custom_pricing: false, is_addon: false, cta_type: "choose", active: true, display_order: 0,
  highlights: [], quotas: Object.fromEntries([...QUOTA_FIELDS.map(([k]) => [k, ""]), ["gsa_available", true]]),
});

const Field = ({ label, children }) => (
  <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</label>{children}</div>
);
const input = "h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]";

function PlanForm({ open, plan, onClose, onSaved }) {
  const [form, setForm] = useState(emptyPlan());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(plan ? {
      ...plan,
      monthly_price: plan.monthly_price ?? "",
      annual_price: plan.annual_price ?? "",
      highlights: plan.highlights || [],
      quotas: { ...emptyPlan().quotas, ...(plan.quotas || {}) },
    } : emptyPlan());
  }, [open, plan]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQuota = (k, v) => setForm((f) => ({ ...f, quotas: { ...f.quotas, [k]: v } }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      monthly_price: form.monthly_price === "" ? null : parseFloat(form.monthly_price),
      annual_price: form.annual_price === "" ? null : parseFloat(form.annual_price),
      display_order: parseInt(form.display_order, 10) || 0,
      highlights: Array.isArray(form.highlights) ? form.highlights : String(form.highlights).split("\n").filter(Boolean),
    };
    delete payload.id; delete payload.key; delete payload.updated_at;
    try {
      if (plan) await api.patch(`/admin/pricing/plans/${plan.id}`, payload);
      else await api.post("/admin/pricing/plans", payload);
      toast.success(plan ? "Plan mis à jour" : "Plan créé");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="pricing-form-dialog">
        <DialogHeader><DialogTitle>{plan ? "Modifier le plan" : "Nouveau plan"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Formulaire de configuration d'un plan tarifaire</DialogDescription>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom du plan"><input data-testid="plan-name" className={input} value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Ordre d'affichage"><input data-testid="plan-order" type="number" className={input} value={form.display_order} onChange={(e) => set("display_order", e.target.value)} /></Field>
            <Field label="Prix mensuel"><input data-testid="plan-monthly" type="number" className={input} value={form.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} placeholder="ex. 49" /></Field>
            <Field label="Prix annuel"><input data-testid="plan-annual" type="number" className={input} value={form.annual_price} onChange={(e) => set("annual_price", e.target.value)} placeholder="ex. 490" /></Field>
            <Field label="Devise"><input data-testid="plan-currency" className={input} value={form.currency} onChange={(e) => set("currency", e.target.value)} /></Field>
            <Field label="Type de CTA">
              <Select value={form.cta_type} onValueChange={(v) => set("cta_type", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="plan-cta"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="choose">Choose plan</SelectItem><SelectItem value="contact">Contact us</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-6 rounded-lg bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="plan-custom" checked={form.custom_pricing} onCheckedChange={(v) => set("custom_pricing", v)} /> Custom pricing</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="plan-addon" checked={form.is_addon} onCheckedChange={(v) => set("is_addon", v)} /> Option (add-on)</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="plan-active" checked={form.active} onCheckedChange={(v) => set("active", v)} /> Actif</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="plan-gsa" checked={form.quotas.gsa_available} onCheckedChange={(v) => setQuota("gsa_available", v)} /> GSA Worldwide disponible</label>
          </div>

          <Field label="Points forts (un par ligne — inclure « populaire » pour mettre en avant)">
            <Textarea data-testid="plan-highlights" className="min-h-[60px] text-sm"
              value={(form.highlights || []).join("\n")}
              onChange={(e) => set("highlights", e.target.value.split("\n"))} />
          </Field>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Quotas & fonctionnalités</p>
            <div className="grid grid-cols-2 gap-3">
              {QUOTA_FIELDS.map(([k, label]) => (
                <Field key={k} label={label}><input data-testid={`quota-${k}`} className={input} value={form.quotas[k] || ""} onChange={(e) => setQuota(k, e.target.value)} /></Field>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuler</button>
          <button data-testid="plan-save" onClick={save} disabled={saving || !form.name} className="rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-50">{saving ? "…" : "Enregistrer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPricing() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => { api.get("/admin/pricing/plans").then((r) => setPlans(r.data.plans || [])).catch(() => setPlans([])); }, []);
  useEffect(() => { load(); }, [load]);
  if (plans === null) return <Loading />;

  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/admin/pricing/plans/${toDelete.id}`); toast.success("Plan supprimé"); setToDelete(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
    finally { setDeleting(false); }
  };

  const priceStr = (p) => p.custom_pricing ? "Custom" : (p.monthly_price != null ? `${p.monthly_price} ${p.currency}/mois` : "—");

  return (
    <div data-testid="admin-pricing">
      <div className="mb-5 flex items-start justify-between gap-4">
        <SectionHeader title="Pricing & Plans" subtitle="Configurez les plans, prix, quotas et fonctionnalités affichés sur la page Pricing." />
        <button data-testid="pricing-new" onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-3 py-2 text-xs font-semibold text-white hover:bg-[#2C5540]">
          <Plus size={14} /> Nouveau plan
        </button>
      </div>

      {plans.length === 0 ? <Empty label="Aucun plan configuré." testid="pricing-empty" /> : (
        <Card>
          <SelectableTable rows={plans} rowId={(p) => p.id} filename="adminpricing.csv" ariaLabel="Tableau — sélection et défilement">
            <table className="w-full min-w-[760px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-3 py-2"><SelectAllHeaderCheckbox /></th>
                <th className="px-5 py-3">Ordre</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Prix</th><th className="px-5 py-3">CTA</th><th className="px-5 py-3">Statut</th><th className="px-5 py-3"></th>
              </tr></thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id} data-testid={`pricing-plan-${p.id}`} className="border-b border-slate-100 last:border-0"><td className="px-3 py-2"><RowCheckbox id={p.id} /></td>
                    <td className="px-5 py-3 text-sm text-slate-500">{p.display_order}</td>
                    <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{p.name}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{p.is_addon ? "Option" : "Plan"}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{priceStr(p)}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{p.cta_type === "contact" ? "Contact us" : "Choose plan"}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Actif" : "Inactif"}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`pricing-edit-${p.id}`} onClick={() => setEditing(p)} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"><Pencil size={14} /></button>
                        <button data-testid={`pricing-delete-${p.id}`} onClick={() => setToDelete(p)} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SelectableTable>
        </Card>
      )}

      <PlanForm open={editing !== undefined} plan={editing || null} onClose={() => setEditing(undefined)} onSaved={load} />
      <ConfirmDialog
        open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Supprimer le plan" destructive loading={deleting}
        description={toDelete ? `Supprimer définitivement le plan « ${toDelete.name} » ? Il disparaîtra de la page Pricing. Cette action est journalisée.` : ""}
        confirmLabel="Supprimer" onConfirm={doDelete}
      />
    </div>
  );
}
